import { apiClient } from "@/lib/apiClient";
import { type SessionAnalysisResult } from "@/types/sessionAnalysis";

export interface Session {
  id: string;
  companyId: string;
  company: {
    id: string;
    name: string;
  };
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  type: "call" | "web_voice" | "web_chat";
  status: "active" | "completed" | "cancelled";
  fromNumber?: string;
  toNumberId?: string;
  toNumber?: {
    id: string;
    e164Number: string;
    friendlyName: string;
  };
  externalStreamId?: string;
  duration?: number;
  model?: string;
  voice?: string;
  toolsUsed: string[];
  systemPrompt: string;
  transcripts: Transcript[];
  _count: {
    transcripts: number;
  };
  analyzer?: string;
  analysis?: SessionAnalysisResult;
  analyzedAt?: string;
  sessionTools: SessionTool[];
  direction?: "inbound" | "outbound";
  outboundCall?: {
    id: string;
    callSid: string;
    toNumber: string;
    status: string;
    initiatedBy: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    createdAt: string;
  };
  callTransfers?: Array<{
    id: string;
    conferenceSid: string;
    transferTo: string;
    reason: string;
    status: string;
    initiatedAt: string;
    connectedAt?: string;
    completedAt?: string;
  }>;
}

export interface SessionTool {
  id: string;
  sessionId: string;
  name: string;
  arguments: any;
  result: any;
}

export interface Transcript {
  id: string;
  sessionId: string;
  createdAt: string;
  updatedAt: string;
  type: "text" | "audio" | "tool";
  content: string;
  source: "user" | "system" | "model";
}

export interface CreateSessionRequest {
  companyId: string;
  type: "call" | "web_voice" | "web_chat";
  status?: "active" | "completed" | "cancelled";
  fromNumber?: string;
  toNumberId?: string;
  externalStreamId?: string;
  userId?: string;
  model?: string;
  voice?: string;
  toolsUsed?: string[];
  systemPrompt?: string;
}

export interface UpdateSessionRequest {
  status?: "active" | "completed" | "cancelled";
  duration?: number;
  fromNumber?: string;
  toNumberId?: string;
  externalStreamId?: string;
  userId?: string;
  model?: string;
  voice?: string;
  toolsUsed?: string[];
  systemPrompt?: string;
}

class SessionApiService {
  // Get all sessions with optional query parameters
  async getSessions(queryParams?: Record<string, string>): Promise<Session[]> {
    const params = new URLSearchParams(queryParams);
    const endpoint = `/v1/sessions${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    return apiClient.get<Session[]>(endpoint);
  }

  // Get sessions for a specific company
  async getSessionsByCompany(
    companyId: string,
    queryParams?: { sortBy?: string; sortOrder?: string }
  ): Promise<Session[]> {
    const params = new URLSearchParams();

    if (queryParams) {
      if (queryParams.sortBy) params.append("sortBy", queryParams.sortBy);
      if (queryParams.sortOrder)
        params.append("sortOrder", queryParams.sortOrder);
    }

    const endpoint = `/v1/companies/${companyId}/sessions${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    return apiClient.get<Session[]>(endpoint);
  }

  // Get a single session by ID
  async getSession(id: string): Promise<Session> {
    return apiClient.get<Session>(`/v1/sessions/${id}`);
  }

  // Create a new session
  async createSession(data: CreateSessionRequest): Promise<Session> {
    return apiClient.post<Session>(`/v1/sessions`, { data });
  }

  // Update a session
  async updateSession(
    id: string,
    data: UpdateSessionRequest
  ): Promise<Session> {
    return apiClient.put<Session>(`/v1/sessions/${id}`, { data });
  }

  // Delete a session
  async deleteSession(id: string): Promise<void> {
    return apiClient.delete<void>(`/v1/sessions/${id}`);
  }
}

export const sessionApi = new SessionApiService();
